import React, { useEffect, useState, useCallback } from "react";
import { Users, Eye, Clock, Radio, TrendingUp } from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { api } from "@/lib/api";

const GOLD = "#c8a24a";
const PALETTE = ["#c8a24a", "#8a7a4e", "#d8c690", "#6b5e3a", "#e8dcae", "#4a4130"];
const RANGES = [{ d: 7, l: "7D" }, { d: 30, l: "30D" }, { d: 90, l: "90D" }];

function fmtDuration(s) {
  if (!s) return "0s";
  const m = Math.floor(s / 60), r = s % 60;
  return m ? `${m}m ${r}s` : `${r}s`;
}

function KPI({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="border border-primary-gold/15 p-6 bg-charcoal" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-text mb-3">
        <Icon size={15} strokeWidth={1.5} className={accent ? "text-luxury-gold" : ""} />
        <span className="text-xs tracking-wide uppercase">{label}</span>
      </div>
      <div className={`serif-italic text-4xl ${accent ? "text-luxury-gold" : "text-ivory"}`}>{value}</div>
    </div>
  );
}

function Panel({ title, children, testid }) {
  return (
    <div className="border border-white/5 bg-charcoal p-6" data-testid={testid}>
      <h3 className="text-xs tracking-wide uppercase text-primary-gold mb-5">{title}</h3>
      {children}
    </div>
  );
}

function BarList({ items, total }) {
  const max = Math.max(...items.map((i) => i.count), 1);
  if (!items.length) return <p className="text-muted-text text-sm">No data yet.</p>;
  return (
    <div className="space-y-3">
      {items.map((i) => (
        <div key={i.name}>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-soft-stone truncate mr-3">{i.name}</span>
            <span className="text-muted-text shrink-0">{i.count}{total ? ` · ${Math.round((i.count / total) * 100)}%` : ""}</span>
          </div>
          <div className="h-1.5 bg-obsidian"><div className="h-full bg-primary-gold/70" style={{ width: `${(i.count / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function Donut({ items }) {
  if (!items.length) return <p className="text-muted-text text-sm">No data yet.</p>;
  return (
    <div className="flex items-center gap-4">
      <ResponsiveContainer width={140} height={140}>
        <PieChart>
          <Pie data={items} dataKey="count" nameKey="name" innerRadius={40} outerRadius={65} paddingAngle={2} stroke="none">
            {items.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip contentStyle={{ background: "#14130f", border: "1px solid rgba(200,162,74,0.3)", fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
      <div className="space-y-2 flex-1">
        {items.map((e, i) => (
          <div key={e.name} className="flex items-center gap-2 text-sm">
            <span className="w-2.5 h-2.5" style={{ background: PALETTE[i % PALETTE.length] }} />
            <span className="text-soft-stone flex-1">{e.name}</span>
            <span className="text-muted-text">{e.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/analytics/summary", { params: { days } });
      setD(data); setActive(data.active_now);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(async () => {
      try { const { data } = await api.get("/admin/analytics/active"); setActive(data.active_now); } catch {}
    }, 15000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <h1 className="text-3xl font-light text-ivory">Analytics</h1>
        <div className="flex gap-2">
          {RANGES.map((r) => (
            <button key={r.d} onClick={() => setDays(r.d)} data-testid={`range-${r.d}`}
              className={`px-4 py-2 text-xs uppercase tracking-wide border transition-colors ${days === r.d ? "border-primary-gold text-primary-gold" : "border-white/10 text-muted-text hover:text-soft-stone"}`}>{r.l}</button>
          ))}
        </div>
      </div>

      {loading && !d ? (
        <p className="text-muted-text">Loading analytics…</p>
      ) : !d ? (
        <p className="text-muted-text">No analytics data available.</p>
      ) : (
        <div className="space-y-8">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <KPI icon={Radio} label="Active Now" value={active} accent testid="kpi-active" />
            <KPI icon={Eye} label="Pageviews" value={d.total_views} testid="kpi-views" />
            <KPI icon={Users} label="Unique Visitors" value={d.unique_visitors} testid="kpi-visitors" />
            <KPI icon={TrendingUp} label="Sessions" value={d.sessions} testid="kpi-sessions" />
            <KPI icon={Clock} label="Avg. Session" value={fmtDuration(d.avg_session_seconds)} testid="kpi-duration" />
          </div>

          <Panel title="Traffic Over Time" testid="panel-timeseries">
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={d.timeseries} margin={{ left: -20, right: 10, top: 10 }}>
                <defs>
                  <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={GOLD} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" tick={{ fill: "#8a8578", fontSize: 11 }} tickFormatter={(v) => v?.slice(5)} />
                <YAxis tick={{ fill: "#8a8578", fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#14130f", border: "1px solid rgba(200,162,74,0.3)", fontSize: 12 }} />
                <Area type="monotone" dataKey="views" stroke={GOLD} strokeWidth={2} fill="url(#gv)" name="Views" />
                <Area type="monotone" dataKey="visitors" stroke="#d8c690" strokeWidth={1} fillOpacity={0} name="Visitors" />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Panel title="Top Pages" testid="panel-top-pages"><BarList items={d.top_pages} total={d.total_views} /></Panel>
            <Panel title="Top Referrers" testid="panel-referrers"><BarList items={d.top_referrers} total={d.total_views} /></Panel>
            <Panel title="Devices" testid="panel-devices"><Donut items={d.devices} /></Panel>
            <Panel title="Browsers" testid="panel-browsers"><Donut items={d.browsers} /></Panel>
            <Panel title="Geography" testid="panel-geography"><BarList items={d.countries} /></Panel>
            <div className="grid grid-cols-1 gap-6">
              <Panel title="Entry Pages" testid="panel-entry"><BarList items={d.entry_pages} /></Panel>
              <Panel title="Exit Pages" testid="panel-exit"><BarList items={d.exit_pages} /></Panel>
            </div>
          </div>

          <Panel title="Recent Sessions" testid="panel-sessions">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-text text-xs uppercase tracking-wide border-b border-white/5">
                    <th className="text-left py-2 font-normal">Entry</th>
                    <th className="text-left py-2 font-normal">Exit</th>
                    <th className="text-left py-2 font-normal">Views</th>
                    <th className="text-left py-2 font-normal">Duration</th>
                    <th className="text-left py-2 font-normal">Device</th>
                    <th className="text-left py-2 font-normal">Country</th>
                    <th className="text-left py-2 font-normal">Source</th>
                    <th className="text-left py-2 font-normal">When</th>
                  </tr>
                </thead>
                <tbody>
                  {d.recent_sessions.length === 0 && <tr><td colSpan={8} className="py-4 text-muted-text">No sessions yet.</td></tr>}
                  {d.recent_sessions.map((s) => (
                    <tr key={s.session_id} className="border-b border-white/5 text-soft-stone" data-testid="session-row">
                      <td className="py-2 pr-3 truncate max-w-[120px]">{s.entry_path}</td>
                      <td className="py-2 pr-3 truncate max-w-[120px]">{s.exit_path}</td>
                      <td className="py-2 pr-3">{s.pageview_count}</td>
                      <td className="py-2 pr-3">{fmtDuration(s.duration_seconds)}</td>
                      <td className="py-2 pr-3">{s.device}</td>
                      <td className="py-2 pr-3">{s.country}</td>
                      <td className="py-2 pr-3">{s.referrer_domain}</td>
                      <td className="py-2 pr-3 text-muted-text whitespace-nowrap">{new Date(s.last_seen).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
